import { useGetLightCartQuery } from '@/redux/api/client/cart';
import Icon from '../Icon';
import classnames from 'classnames';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import Shimmer from '../Shimmer';
import { useCart } from './hooks/useCart';
import Link from '../Link';

interface MyCartProps {
  context: 'panel' | 'page';
}

const MyCart = (props: MyCartProps) => {
  const { cartData, isLoading: isCartLoading, deleteCartItem } = useCart();
  const { data: lightCart } = useGetLightCartQuery();
  const isPanelContext = props.context === 'panel';
  const isPageContext = props.context === 'page';
  const md = useMediaQuery(ScreenSizeQueries.md);

  const containerStyles = classnames(
    { 'flex h-full flex-col px-16': isPanelContext },
    { 'container pt-20': isPageContext }
  );

  const cartItemsStyles = classnames(
    { 'w-3/5': isPageContext && md },
    { 'w-full': isPageContext && !md }
  );

  const cartLineItemsStyles = (index: number) =>
    classnames('max-w-full border-t border-solid border-gray-light py-6', {
      'border-t-0': isPageContext && index === 0
    });

  const handleDeleteCartItem = (code: string) => {
    deleteCartItem(code);
  };

  const cartItemsShimmer = (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <div className={cartLineItemsStyles(i)} key={`shimmer-${i}`}>
          <div className="mb-4">
            <Shimmer className="h-4 w-24" />
          </div>
          <div className="mb-4 flex">
            <div className="flex w-[70%] flex-col gap-1">
              <Shimmer className="h-5 w-full" />
              <Shimmer className="h-5 w-64" />
            </div>
            <div className="w-[10%]"></div>
            <div className="flex w-1/5 flex-col items-end text-right">
              <div
                className={`flex items-end gap-1 ${isPageContext && md ? 'flex-row' : 'flex-col-reverse'}`}
              >
                <Shimmer className="h-5 w-14" />
                <Shimmer className="h-5 w-16" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Shimmer className="h-5 w-8" />
            <Shimmer className="h-5 w-6 rounded" />
          </div>
        </div>
      ))}
    </>
  );

  const cartItems = (
    <>
      {cartData?.orderLines?.map((item, i) => (
        <div className={cartLineItemsStyles(i)} key={i}>
          <div className="text-2 mb-4 text-gray">{item.displayType}</div>
          <div className="mb-4 flex">
            <div className="text-1 w-[70%]">
              <Link
                href={item.productVariantUrl ?? ''}
                text={item.displayName ?? ''}
                title={item.displayName ?? ''}
                variant="text-link-1"
                className="no-underline"
              />
            </div>
            <div className="w-[10%]"></div>
            <div className="text-1 flex w-1/5 flex-col items-end text-right">
              <div
                className={`flex items-end gap-1 ${isPageContext && md ? 'flex-row' : 'flex-col-reverse'}`}
              >
                <span
                  className={classnames('font-[400] text-gray line-through', {
                    hidden: item.extendedPrice === item.discountPrice
                  })}
                >
                  ${item.extendedPrice?.toFixed(2)}
                </span>
                <span className="pl-2 font-[500]">${item.discountPrice?.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="text-1 flex items-center justify-between">
            <div>{item.quantity}</div>
            <button onClick={() => handleDeleteCartItem(item.code!)}>
              <Icon name="delete" size="large" className="cursor-pointer" />
            </button>
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className={containerStyles}>
      <h1 className="heading-4 mb-8">
        My Cart <span className="text-1 pt-1 align-top">({lightCart?.extendedQuantity ?? 0})</span>
      </h1>
      <div className={cartItemsStyles}>{isCartLoading ? cartItemsShimmer : cartItems}</div>
    </div>
  );
};

export default MyCart;
